import client from "./client";

type AuditLogParams = {
  skip?: number;
  limit?: number;
  userId?: string;
  action?: string;
  resourceType?: string;
  dateFrom?: string;
  dateTo?: string;
};

export const adminApi = {
  listUsers: () => client.get("/admin/users"),
  getUser: (id: string) => client.get(`/admin/users/${id}`),
  createUser: (data: Record<string, unknown>) => client.post("/admin/users", data),
  updateUser: (id: string, data: Record<string, unknown>) => client.put(`/admin/users/${id}`, data),
  deactivateUser: (id: string) => client.delete(`/admin/users/${id}`),
  deleteUserPermanent: (id: string, confirmEmail: string) =>
    client.delete(`/admin/users/${id}/permanent`, { data: { confirm_email: confirmEmail } }),
  resetUserPassword: (id: string, newPassword: string) =>
    client.post(`/admin/users/${id}/reset-password`, { new_password: newPassword }),
  resetUser2fa: (id: string) => client.post(`/admin/users/${id}/reset-2fa`),
  resendInvite: (id: string) => client.post(`/admin/users/${id}/resend-invite`),
  getUserTokenHistory: (id: string, days = 30) =>
    client.get(`/admin/users/${id}/token-history`, { params: { days } }),

  getTokenStats: () => client.get("/admin/users/token-stats"),

  getAnalytics: (days = 30) => client.get("/admin/analytics", { params: { days } }),

  getSettings: () => client.get("/admin/settings"),
  upsertSetting: (key: string, value: string | null) => client.put(`/admin/settings/${key}`, { value }),

  getAuditLog: (params: AuditLogParams = {}) => {
    const { skip = 0, limit = 50, userId, action, resourceType, dateFrom, dateTo } = params;
    return client.get("/admin/audit-log", {
      params: {
        skip,
        limit,
        ...(userId ? { user_id: userId } : {}),
        ...(action ? { action } : {}),
        ...(resourceType ? { resource_type: resourceType } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      },
    });
  },
  getAuditLogDetail: (id: string) => client.get(`/admin/audit-log/${id}`),

  getTrendingQuestions: (days = 30) =>
    client.get("/admin/trending-questions", { params: { days } }),
  getSuggestedQuestions: () => client.get("/admin/suggested-questions"),
  pinQuestion: (question: string) =>
    client.post("/admin/suggested-questions", { question }),
  unpinQuestion: (id: string) => client.delete(`/admin/suggested-questions/${id}`),

  listSchedules: () => client.get("/reports/schedules"),
  createSchedule: (data: Record<string, unknown>) => client.post("/reports/schedules", data),
  updateSchedule: (id: string, data: Record<string, unknown>) => client.put(`/reports/schedules/${id}`, data),
  sendNow: (id: string) => client.post(`/reports/schedules/${id}/send-now`),

  listSubscriptions: (scheduleId: string) =>
    client.get("/reports/subscriptions", { params: { schedule_id: scheduleId } }),
  createSubscription: (data: Record<string, unknown>) => client.post("/reports/subscriptions", data),
  deleteSubscription: (id: string) => client.delete(`/reports/subscriptions/${id}`),

  listTeams: () => client.get("/admin/teams"),
  createTeam: (data: { name: string; description?: string }) => client.post("/admin/teams", data),
  getTeam: (id: string) => client.get(`/admin/teams/${id}`),
  updateTeam: (id: string, data: { name?: string; description?: string }) => client.put(`/admin/teams/${id}`, data),
  deleteTeam: (id: string) => client.delete(`/admin/teams/${id}`),
  addTeamMember: (teamId: string, userId: string) => client.post(`/admin/teams/${teamId}/members/${userId}`),
  removeTeamMember: (teamId: string, userId: string) => client.delete(`/admin/teams/${teamId}/members/${userId}`),
  addTeamDashboard: (teamId: string, dashboardId: number) => client.post(`/admin/teams/${teamId}/dashboards/${dashboardId}`),
  removeTeamDashboard: (teamId: string, dashboardId: number) => client.delete(`/admin/teams/${teamId}/dashboards/${dashboardId}`),
  addTeamReport: (teamId: string, reportId: string) => client.post(`/admin/teams/${teamId}/reports/${reportId}`),
  removeTeamReport: (teamId: string, reportId: string) => client.delete(`/admin/teams/${teamId}/reports/${reportId}`),

  // Scheduled actions (all authenticated users can view; cancel rights depend on role)
  listScheduledActions: (status?: string) =>
    client.get("/scheduled-actions", { params: status ? { status } : undefined }),
  cancelScheduledAction: (id: string) => client.post(`/scheduled-actions/${id}/cancel`),

  // Tenant management (super_admin only)
  listTenants: () => client.get("/tenants"),
  createTenant: (data: { name: string; slug: string }) => client.post("/tenants", data),
  getTenant: (id: string) => client.get(`/tenants/${id}`),
  updateTenant: (id: string, data: { name?: string; is_active?: boolean }) => client.put(`/tenants/${id}`, data),
  getTenantSlack: (id: string) => client.get(`/tenants/${id}/slack`),
  updateTenantSlack: (id: string, data: { bot_token?: string; signing_secret?: string; channel_ids?: string }) =>
    client.put(`/tenants/${id}/slack`, data),
  listTenantUsers: (id: string) => client.get(`/tenants/${id}/users`),
};
